import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Download, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDT = (d, t) => (d ? `${d}${t ? " · " + t : ""}` : "To be arranged");

const Cell = ({ label, value, testid }) => (
  <div className="py-2.5 px-1">
    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
    <p className="text-sm font-bold text-[#0b132b] mt-0.5" data-testid={testid}>{value || "-"}</p>
  </div>
);

export const AdminTicketPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () =>
    axios
      .get(`${API}/enquiries/${id}/ticket`, { withCredentials: true })
      .then((r) => setTicket(r.data))
      .catch(() => setError(true));

  useEffect(() => {
    if (user && user.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  const send = async () => {
    if (!window.confirm(`Send the ticket to ${ticket.customer.email}? A copy goes to the office.`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/enquiries/${id}/ticket/send`, {}, { withCredentials: true });
      setTicket({ ...ticket, ticket_sent_at: r.data.ticket_sent_at });
      toast.success(`Ticket ${r.data.ref} emailed to the guest (copy to office).`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the ticket email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="w-10 h-10 rounded-full border-4 border-[var(--brand)]/20 border-t-[var(--brand)] animate-spin" data-testid="ticket-loading-spinner" />
      </div>
    );
  }
  if (user === false || user.role !== "admin") return <Navigate to="/login" replace />;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-slate-100 text-sm font-bold text-slate-500">Booking not found.</div>;
  if (!ticket) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="w-10 h-10 rounded-full border-4 border-[var(--brand)]/20 border-t-[var(--brand)] animate-spin" />
      </div>
    );
  }

  const p = ticket.pricing;
  const confirmed = ticket.status === "confirmed";
  const payMap = { paid: ["text-emerald-600", "Paid"], deposit: ["text-amber-600", "Deposit received"], unpaid: ["text-red-600", "Unpaid"] };
  const [payCls, payLabel] = payMap[ticket.payment_status] || payMap.unpaid;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="ticket-page">
      {/* Action bar — hidden when printing */}
      <div className="no-print max-w-3xl mx-auto mb-5 flex flex-wrap items-center gap-2.5">
        <Link to="/admin" data-testid="ticket-back-link" className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 text-xs font-bold px-4 py-2.5 text-slate-600 hover:bg-slate-50 transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to bookings
        </Link>
        <button
          data-testid="ticket-download-pdf"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#0b132b] text-white text-xs font-bold px-4 py-2.5 hover:bg-slate-700 transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Download PDF ticket
        </button>
        <button
          data-testid="ticket-send-email"
          onClick={send}
          disabled={sending}
          className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand)] text-white text-xs font-bold px-4 py-2.5 hover:bg-[var(--brand-dark)] transition-colors disabled:opacity-60"
        >
          <Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send ticket by email"}
        </button>
        {ticket.ticket_sent_at && (
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600" data-testid="ticket-sent-badge">
            <CheckCircle2 className="w-4 h-4" /> Sent {ticket.ticket_sent_at.slice(0, 16).replace("T", " ")}
          </span>
        )}
      </div>

      {/* The ticket */}
      <div className="ticket-page max-w-3xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-100" data-testid="ticket-card">
        <div className="px-8 pt-8 pb-5 text-center border-b border-slate-100">
          <img src={ticket.logo_url || "/logo.png"} alt="Voyage Switzerland" className="h-16 mx-auto mb-2 object-contain" data-testid="ticket-logo" />
          <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-slate-400">Booking Ticket</p>
        </div>

        <div className={`py-2.5 text-center text-xs font-bold tracking-widest uppercase text-white ${confirmed ? "bg-emerald-600" : "bg-amber-600"}`} data-testid="ticket-status-banner">
          {confirmed ? "Booking Confirmed" : `Booking ${ticket.status}`}
        </div>

        <div className="px-6 sm:px-8 pt-6">
          {ticket.photo_url && (
            <img src={ticket.photo_url} alt={ticket.item?.name} className="w-full h-56 sm:h-64 object-cover rounded-xl mb-4" data-testid="ticket-item-photo" />
          )}
          <h1 className="font-display font-extrabold text-2xl text-[#0b132b]" data-testid="ticket-item-name">{ticket.item?.name}</h1>
          {ticket.item?.duration && <p className="text-sm text-slate-500 font-semibold mt-0.5">{ticket.item.duration}</p>}
        </div>

        <div className="mx-6 sm:mx-8 mt-5 rounded-2xl bg-slate-50 border border-slate-100 grid grid-cols-3 divide-x divide-slate-200 text-center" data-testid="ticket-price-summary">
          <div className="py-4">
            <p className="text-2xl font-display font-extrabold text-[var(--brand)]" data-testid="ticket-days">{ticket.schedule.days ?? "—"}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">day{ticket.schedule.days > 1 ? "s" : ""}</p>
          </div>
          <div className="py-4 px-2">
            <p className="text-sm font-bold text-[#0b132b]" data-testid="ticket-calculation">
              {p.priced ? `${p.qty_label} × ${p.currency} ${Number(p.unit_price).toLocaleString()} ${p.unit_label}`.trim() : "On request"}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">calculation</p>
          </div>
          <div className="py-4 px-2">
            <p className="text-xl font-display font-extrabold text-[#0b132b]" data-testid="ticket-total">
              {p.priced ? `${p.currency} ${Number(p.total).toLocaleString()}` : "On request"}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">total</p>
          </div>
        </div>

        <div className="px-6 sm:px-8 py-5 grid grid-cols-2 sm:grid-cols-3 gap-x-4 divide-y-0" data-testid="ticket-details-grid">
          <Cell label="Booking reference" value={ticket.ref} testid="ticket-ref" />
          <Cell label="Guest" value={ticket.customer.name} testid="ticket-guest" />
          <Cell label="Email" value={ticket.customer.email} testid="ticket-email" />
          <Cell label="Phone" value={ticket.customer.phone} testid="ticket-phone" />
          <Cell label="Guests" value={ticket.guests || "1"} testid="ticket-guests" />
          <Cell label="Pickup" value={fmtDT(ticket.schedule.pickup_date, ticket.schedule.pickup_time)} testid="ticket-pickup" />
          <Cell label="Pickup location" value={ticket.schedule.pickup_location || "To be arranged"} testid="ticket-pickup-location" />
          <Cell label="Return / drop-off" value={fmtDT(ticket.schedule.return_date, ticket.schedule.return_time)} testid="ticket-return" />
          <Cell label="Drop-off location" value={ticket.schedule.dropoff_location || "To be arranged"} testid="ticket-dropoff-location" />
          {ticket.driver && <Cell label="Your driver" value={`${ticket.driver.name}${ticket.driver.phone ? " · " + ticket.driver.phone : ""}`} testid="ticket-driver" />}
          <div className="py-2.5 px-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Payment status</p>
            <p className={`text-sm font-bold mt-0.5 ${payCls}`} data-testid="ticket-payment-status">{payLabel}</p>
          </div>
        </div>

        {ticket.message && (
          <div className="mx-6 sm:mx-8 mb-5 rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
            <p className="text-xs text-slate-500"><span className="font-bold text-[#0b132b]">Notes &amp; requests:</span> {ticket.message}</p>
          </div>
        )}

        <div className="bg-[#0b132b] px-8 py-5">
          <p className="text-xs text-slate-300">
            Questions? Reply to the ticket email or reach us at {ticket.contact.email}
            {ticket.contact.phone ? ` · ${ticket.contact.phone}` : ""}.
          </p>
          <p className="text-[10px] text-slate-500 mt-1.5">
            Booking reference {ticket.ref} · Voyage Switzerland — we never ask for your password or card details by email.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminTicketPage;
